import { useState } from 'react'
import type { FormEvent } from 'react'
import { catalogo, promociones as apiPromociones } from '../api/servicios'
import { fecha, dinero } from '../api/formato'
import { desdeCampoLocal, paraCampoLocal } from '../api/zona'
import type { Categoria, Producto, Promocion, TipoPromocion } from '../api/tipos'
import { BuscadorProducto } from '../componentes/BuscadorProducto'
import { Dialogo } from '../componentes/Dialogo'
import { Estado } from '../componentes/Estado'
import { usarRetardo } from '../componentes/Paginador'
import { mensajeDeError, usarCargar } from '../componentes/usarCargar'
import './listados.css'
import { idiomaNegocio } from '../api/moneda'

type Ventana =
    | { tipo: 'ninguno' }
    | { tipo: 'nueva' }
    | { tipo: 'editar'; promocion: Promocion }

const TIPOS: { valor: TipoPromocion; nombre: string; ayuda: string }[] = [
    {
        valor: 'PrecioPorCantidad',
        nombre: 'Precio por cantidad',
        ayuda: 'Llevando 3 kilos se cobran $5.000. Lo que sobre del paquete se cobra al precio normal.',
    },
    {
        valor: 'PrecioEscalonado',
        nombre: 'Precio rebajado desde una cantidad',
        ayuda: 'Desde 5 kilos, cada kilo baja a $1.800. La rebaja alcanza a todas las unidades, no solo a las que pasan del mínimo.',
    },
    {
        valor: 'Porcentaje',
        nombre: 'Porcentaje de descuento',
        ayuda: 'Un porcentaje fijo sobre el total de la línea.',
    },
]

function nombreTipo(tipo: TipoPromocion): string {
    return TIPOS.find((t) => t.valor === tipo)?.nombre ?? tipo
}

/**
 * Cantidad sin decimales de relleno.
 *
 * La promoción no sabe en qué unidad se vende el producto, así que no se puede
 * fijar la cantidad de decimales por adelantado. Y con tres fijos, «6 panes» se
 * imprime «6,000», que en Chile se lee como seis mil.
 */
const formateadores = new Map<string, Intl.NumberFormat>()

function cantidadLibre(valor: number): string {
    // Se arma al usarlo y no al cargar el modulo: el idioma del negocio llega
    // con la configuracion, despues de que este archivo se importa.
    const idioma = idiomaNegocio()
    let f = formateadores.get(idioma)

    if (!f) {
        f = new Intl.NumberFormat(idioma, { minimumFractionDigits: 0, maximumFractionDigits: 3 })
        formateadores.set(idioma, f)
    }

    return f.format(valor)
}

/** Una frase que describe la promoción como la explicaría el dueño del local. */
export function describir(p: Promocion): string {
    switch (p.tipo) {
        case 'PrecioPorCantidad':
            return `${cantidadLibre(p.cantidadMinima ?? 0)} por ${dinero(p.precioPaquete)}`
        case 'PrecioEscalonado':
            return `desde ${cantidadLibre(p.cantidadMinima ?? 0)}, a ${dinero(p.precioUnitario)} c/u`
        case 'Porcentaje':
            return `${p.porcentaje ?? 0}% de descuento`
        default:
            return '—'
    }
}

export function Promociones() {
    const [texto, setTexto] = useState('')
    const [soloVigentes, setSoloVigentes] = useState(false)
    const [ventana, setVentana] = useState<Ventana>({ tipo: 'ninguno' })

    const busqueda = usarRetardo(texto)

    const categorias = usarCargar(() => catalogo.categorias(), [])
    const lista = usarCargar(
        () => apiPromociones.listar({ texto: busqueda || undefined, soloVigentes }),
        [busqueda, soloVigentes],
    )

    function refrescar() {
        setVentana({ tipo: 'ninguno' })
        lista.recargar()
    }

    const items = lista.datos ?? []

    return (
        <div className="pagina-listado columna g4">
            <header className="fila entre envuelve g3">
                <h1>Promociones</h1>

                <button
                    type="button"
                    className="boton boton-primario"
                    onClick={() => setVentana({ tipo: 'nueva' })}
                >
                    Nueva promoción
                </button>
            </header>

            <div className="aviso aviso-info">
                Las promociones se aplican solas al cobrar: el cajero no tiene que acordarse de
                ninguna. Lo que descuenten sale por separado en el cierre de caja y en el reporte de
                promociones.
            </div>

            <section className="panel columna g3">
                <div className="fila envuelve g3 entre">
                    <input
                        className="campo"
                        type="search"
                        placeholder="Buscar por nombre"
                        value={texto}
                        onChange={(e) => setTexto(e.target.value)}
                    />

                    <label className="fila g2 interruptor">
                        <input
                            type="checkbox"
                            checked={soloVigentes}
                            onChange={(e) => setSoloVigentes(e.target.checked)}
                        />
                        <span>Solo las que están corriendo hoy</span>
                    </label>
                </div>

                <Estado
                    cargando={lista.cargando}
                    error={lista.error}
                    alReintentar={lista.recargar}
                    vacio={items.length === 0}
                    mensajeVacio="No hay promociones cargadas."
                >
                    <div className="tabla-envoltura">
                        <table className="tabla">
                            <thead>
                                <tr>
                                    <th>Promoción</th>
                                    <th className="oculto-angosto">Se aplica a</th>
                                    <th className="oculto-angosto">Vigencia</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((p) => (
                                    <tr key={p.promocionId}>
                                        <td>
                                            <div className="columna">
                                                <span className="fila envuelve g2">
                                                    <span>{p.nombre}</span>
                                                    {p.vigente ? (
                                                        <span className="insignia insignia-ok">
                                                            corriendo
                                                        </span>
                                                    ) : (
                                                        <span className="insignia">
                                                            {p.activa ? 'fuera de fecha' : 'apagada'}
                                                        </span>
                                                    )}
                                                </span>
                                                <span className="tenue diminuto">
                                                    {describir(p)} · {nombreTipo(p.tipo)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="oculto-angosto chico">
                                            {p.productoNombre ??
                                                (p.categoriaNombre
                                                    ? `categoría ${p.categoriaNombre}`
                                                    : '—')}
                                        </td>
                                        <td className="oculto-angosto tenue chico">
                                            <div className="columna">
                                                <span>desde {fecha(p.vigenteDesde)}</span>
                                                <span>
                                                    {p.vigenteHasta
                                                        ? `hasta ${fecha(p.vigenteHasta)}`
                                                        : 'sin fecha de término'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="derecha">
                                            <div className="fila g2 acciones-fila">
                                                <button
                                                    type="button"
                                                    className="boton boton-fantasma"
                                                    onClick={() =>
                                                        setVentana({ tipo: 'editar', promocion: p })
                                                    }
                                                >
                                                    Editar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Estado>
            </section>

            {ventana.tipo === 'nueva' && (
                <DialogoNueva
                    categorias={categorias.datos ?? []}
                    alGuardar={refrescar}
                    alCerrar={() => setVentana({ tipo: 'ninguno' })}
                />
            )}

            {ventana.tipo === 'editar' && (
                <DialogoEditar
                    promocion={ventana.promocion}
                    alGuardar={refrescar}
                    alCerrar={() => setVentana({ tipo: 'ninguno' })}
                />
            )}
        </div>
    )
}

/* -------------------------------------------------------------------------- */

/** Campos que dependen del tipo. Se comparten entre el alta y la edición. */
function CamposDelTipo({
    tipo,
    cantidadMinima,
    setCantidadMinima,
    precioPaquete,
    setPrecioPaquete,
    precioUnitario,
    setPrecioUnitario,
    porcentaje,
    setPorcentaje,
}: {
    tipo: TipoPromocion
    cantidadMinima: string
    setCantidadMinima: (v: string) => void
    precioPaquete: string
    setPrecioPaquete: (v: string) => void
    precioUnitario: string
    setPrecioUnitario: (v: string) => void
    porcentaje: string
    setPorcentaje: (v: string) => void
}) {
    if (tipo === 'Porcentaje') {
        return (
            <div>
                <label className="etiqueta" htmlFor="promo-porcentaje">
                    Porcentaje de descuento
                </label>
                <input
                    id="promo-porcentaje"
                    className="campo"
                    type="number"
                    min={0.01}
                    max={100}
                    step={0.01}
                    value={porcentaje}
                    onChange={(e) => setPorcentaje(e.target.value)}
                />
            </div>
        )
    }

    return (
        <div className="dos-paneles">
            <div>
                <label className="etiqueta" htmlFor="promo-minima">
                    Cantidad mínima
                </label>
                <input
                    id="promo-minima"
                    className="campo"
                    type="number"
                    min={0.001}
                    step={0.001}
                    value={cantidadMinima}
                    onChange={(e) => setCantidadMinima(e.target.value)}
                />
                <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                    En la unidad del producto: kilos, litros o piezas.
                </p>
            </div>

            {tipo === 'PrecioPorCantidad' ? (
                <div>
                    <label className="etiqueta" htmlFor="promo-paquete">
                        Precio por esa cantidad
                    </label>
                    <input
                        id="promo-paquete"
                        className="campo"
                        type="number"
                        min={1}
                        step={1}
                        value={precioPaquete}
                        onChange={(e) => setPrecioPaquete(e.target.value)}
                    />
                </div>
            ) : (
                <div>
                    <label className="etiqueta" htmlFor="promo-unitario">
                        Precio rebajado por unidad
                    </label>
                    <input
                        id="promo-unitario"
                        className="campo"
                        type="number"
                        min={1}
                        step={1}
                        value={precioUnitario}
                        onChange={(e) => setPrecioUnitario(e.target.value)}
                    />
                </div>
            )}
        </div>
    )
}

/* -------------------------------------------------------------------------- */

function DialogoNueva({
    categorias,
    alGuardar,
    alCerrar,
}: {
    categorias: Categoria[]
    alGuardar: () => void
    alCerrar: () => void
}) {
    const [nombre, setNombre] = useState('')
    const [tipo, setTipo] = useState<TipoPromocion>('PrecioPorCantidad')
    const [alcance, setAlcance] = useState<'producto' | 'categoria'>('producto')
    const [producto, setProducto] = useState<Producto | null>(null)
    const [categoriaId, setCategoriaId] = useState('')
    const [cantidadMinima, setCantidadMinima] = useState('')
    const [precioPaquete, setPrecioPaquete] = useState('')
    const [precioUnitario, setPrecioUnitario] = useState('')
    const [porcentaje, setPorcentaje] = useState('')
    const [vigenteHasta, setVigenteHasta] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    const ayuda = TIPOS.find((t) => t.valor === tipo)?.ayuda

    async function guardar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (nombre.trim().length === 0) {
            setError('Póngale un nombre: es lo que verá el cliente en la boleta.')
            return
        }

        if (alcance === 'producto' && !producto) {
            setError('Elija el producto al que se aplica.')
            return
        }

        if (alcance === 'categoria' && !categoriaId) {
            setError('Elija la categoría a la que se aplica.')
            return
        }

        setEnviando(true)

        try {
            await apiPromociones.crear({
                nombre: nombre.trim(),
                tipo,
                productoId: alcance === 'producto' ? producto!.productoId : null,
                categoriaId: alcance === 'categoria' ? Number(categoriaId) : null,
                cantidadMinima: tipo === 'Porcentaje' ? null : Number(cantidadMinima),
                precioPaquete: tipo === 'PrecioPorCantidad' ? Number(precioPaquete) : null,
                precioUnitario: tipo === 'PrecioEscalonado' ? Number(precioUnitario) : null,
                porcentaje: tipo === 'Porcentaje' ? Number(porcentaje) : null,
                // La hora que escribio el usuario es la del local, no la del
                // equipo: se interpreta en la zona del negocio antes de enviarla.
                vigenteHasta: desdeCampoLocal(vigenteHasta),
            })

            alGuardar()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo="Nueva promoción"
            ancho="ancho"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-promocion"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Guardando…' : 'Crear'}
                    </button>
                </>
            }
        >
            <form id="forma-promocion" className="columna g4" onSubmit={guardar}>
                <div>
                    <label className="etiqueta" htmlFor="promo-nombre">
                        Nombre
                    </label>
                    <input
                        id="promo-nombre"
                        className="campo"
                        maxLength={120}
                        placeholder="Harina 3 kg a $5.000"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                    />
                    <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                        Aparece en la boleta junto al descuento.
                    </p>
                </div>

                <div>
                    <label className="etiqueta" htmlFor="promo-tipo">
                        Tipo
                    </label>
                    <select
                        id="promo-tipo"
                        className="campo"
                        value={tipo}
                        onChange={(e) => setTipo(e.target.value as TipoPromocion)}
                    >
                        {TIPOS.map((t) => (
                            <option key={t.valor} value={t.valor}>
                                {t.nombre}
                            </option>
                        ))}
                    </select>
                    {ayuda && (
                        <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                            {ayuda}
                        </p>
                    )}
                </div>

                <CamposDelTipo
                    tipo={tipo}
                    cantidadMinima={cantidadMinima}
                    setCantidadMinima={setCantidadMinima}
                    precioPaquete={precioPaquete}
                    setPrecioPaquete={setPrecioPaquete}
                    precioUnitario={precioUnitario}
                    setPrecioUnitario={setPrecioUnitario}
                    porcentaje={porcentaje}
                    setPorcentaje={setPorcentaje}
                />

                <div className="columna g2">
                    <span className="etiqueta">Se aplica a</span>

                    <div className="fila g3 envuelve">
                        <label className="fila g2 interruptor">
                            <input
                                type="radio"
                                name="alcance"
                                checked={alcance === 'producto'}
                                onChange={() => setAlcance('producto')}
                            />
                            <span>Un producto</span>
                        </label>
                        <label className="fila g2 interruptor">
                            <input
                                type="radio"
                                name="alcance"
                                checked={alcance === 'categoria'}
                                onChange={() => setAlcance('categoria')}
                            />
                            <span>Una categoría entera</span>
                        </label>
                    </div>

                    {alcance === 'producto' ? (
                        producto ? (
                            <div className="fila entre g3 panel-suave">
                                <span className="columna">
                                    <span>{producto.nombre}</span>
                                    <span className="tenue diminuto numero">
                                        {producto.sku} · {dinero(producto.precioVigente)}
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    className="boton boton-fantasma"
                                    onClick={() => setProducto(null)}
                                >
                                    Cambiar
                                </button>
                            </div>
                        ) : (
                            <BuscadorProducto alElegir={setProducto} />
                        )
                    ) : (
                        <select
                            className="campo"
                            aria-label="Categoría"
                            value={categoriaId}
                            onChange={(e) => setCategoriaId(e.target.value)}
                        >
                            <option value="">Elija una categoría…</option>
                            {categorias.map((c) => (
                                <option key={c.categoriaId} value={c.categoriaId}>
                                    {c.nombre}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                <div>
                    <label className="etiqueta" htmlFor="promo-hasta">
                        Termina el <span className="tenue">(opcional)</span>
                    </label>
                    <input
                        id="promo-hasta"
                        className="campo"
                        type="datetime-local"
                        value={vigenteHasta}
                        onChange={(e) => setVigenteHasta(e.target.value)}
                    />
                    <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                        Sin fecha, corre hasta que la apague.
                    </p>
                </div>

                <div className="aviso aviso-atencion">
                    El tipo y a qué se aplica no se pueden cambiar después: las ventas ya cobradas
                    apuntan a esta promoción. Para cambiarlos, cree otra y apague esta.
                </div>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */

function DialogoEditar({
    promocion,
    alGuardar,
    alCerrar,
}: {
    promocion: Promocion
    alGuardar: () => void
    alCerrar: () => void
}) {
    const [nombre, setNombre] = useState(promocion.nombre)
    const [cantidadMinima, setCantidadMinima] = useState(String(promocion.cantidadMinima ?? ''))
    const [precioPaquete, setPrecioPaquete] = useState(String(promocion.precioPaquete ?? ''))
    const [precioUnitario, setPrecioUnitario] = useState(String(promocion.precioUnitario ?? ''))
    const [porcentaje, setPorcentaje] = useState(String(promocion.porcentaje ?? ''))
    const [vigenteHasta, setVigenteHasta] = useState(paraCampoLocal(promocion.vigenteHasta))
    const [activa, setActiva] = useState(promocion.activa)
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    async function guardar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (nombre.trim().length === 0) {
            setError('El nombre es obligatorio.')
            return
        }

        setEnviando(true)

        try {
            await apiPromociones.actualizar(promocion.promocionId, {
                nombre: nombre.trim(),
                cantidadMinima: promocion.tipo === 'Porcentaje' ? null : Number(cantidadMinima),
                precioPaquete:
                    promocion.tipo === 'PrecioPorCantidad' ? Number(precioPaquete) : null,
                precioUnitario:
                    promocion.tipo === 'PrecioEscalonado' ? Number(precioUnitario) : null,
                porcentaje: promocion.tipo === 'Porcentaje' ? Number(porcentaje) : null,
                vigenteHasta: desdeCampoLocal(vigenteHasta),
                activa,
            })

            alGuardar()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo="Editar promoción"
            ancho="ancho"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-promocion-editar"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Guardando…' : 'Guardar'}
                    </button>
                </>
            }
        >
            <form id="forma-promocion-editar" className="columna g4" onSubmit={guardar}>
                <div className="panel-suave columna">
                    <span className="tenue diminuto">
                        {nombreTipo(promocion.tipo)} ·{' '}
                        {promocion.productoNombre ??
                            (promocion.categoriaNombre
                                ? `categoría ${promocion.categoriaNombre}`
                                : '—')}
                    </span>
                    <span className="tenue diminuto">
                        Ni el tipo ni a qué se aplica se pueden cambiar.
                    </span>
                </div>

                <div>
                    <label className="etiqueta" htmlFor="promo-nombre-editar">
                        Nombre
                    </label>
                    <input
                        id="promo-nombre-editar"
                        className="campo"
                        maxLength={120}
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                    />
                </div>

                <CamposDelTipo
                    tipo={promocion.tipo}
                    cantidadMinima={cantidadMinima}
                    setCantidadMinima={setCantidadMinima}
                    precioPaquete={precioPaquete}
                    setPrecioPaquete={setPrecioPaquete}
                    precioUnitario={precioUnitario}
                    setPrecioUnitario={setPrecioUnitario}
                    porcentaje={porcentaje}
                    setPorcentaje={setPorcentaje}
                />

                <div>
                    <label className="etiqueta" htmlFor="promo-hasta-editar">
                        Termina el <span className="tenue">(opcional)</span>
                    </label>
                    <input
                        id="promo-hasta-editar"
                        className="campo"
                        type="datetime-local"
                        value={vigenteHasta}
                        onChange={(e) => setVigenteHasta(e.target.value)}
                    />
                </div>

                <label className="fila g2 interruptor">
                    <input
                        type="checkbox"
                        checked={activa}
                        onChange={(e) => setActiva(e.target.checked)}
                    />
                    <span>Activa</span>
                </label>
                <p className="tenue diminuto">
                    Apagarla deja de aplicarla desde la próxima venta. Las ya cobradas siguen
                    mostrando su nombre.
                </p>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}
